import type { FontAssetMetadata } from '@course-authoring/shared-types';

const MIN_WEIGHT = 1;
const MAX_WEIGHT = 1000;

const isValidWeight = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= MIN_WEIGHT && value <= MAX_WEIGHT;

/**
 * Reads the supported weights from a font file so the upload preview can
 * declare them before the server responds. Mirrors the server-side
 * extraction (author-api fontMetadata): a `wght` axis means variable,
 * otherwise the OS/2 usWeightClass is used and the font is static.
 *
 * Returns null on any failure; the preview then registers without a weight
 * and the server's metadata corrects it once the upload completes.
 * fontkit is loaded on demand so it stays out of the main bundle.
 */
export async function extractFontMetadata(buffer: ArrayBuffer): Promise<FontAssetMetadata | null> {
  try {
    const fontkit = await import('fontkit');
    const parsed = fontkit.create(new Uint8Array(buffer) as unknown as Buffer);
    const font = 'fonts' in parsed ? parsed.fonts[0] : parsed;
    if (!font) return null;

    const wght = font.variationAxes['wght'];
    if (wght && isValidWeight(wght.min) && isValidWeight(wght.max) && wght.min < wght.max) {
      return { weight: { min: Math.round(wght.min), max: Math.round(wght.max) }, variable: true };
    }

    const weightClass = font['OS/2']?.usWeightClass;
    if (isValidWeight(weightClass)) {
      return { weight: { min: weightClass, max: weightClass }, variable: false };
    }
    return null;
  } catch {
    return null;
  }
}
